import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom'; // ✨ Import useSearchParams
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { getAdminPendingApplications, getAdminTutors, getPublicSetting, approveTutorApplication, rejectTutorApplication, pauseTutor, reactivateTutor, rejectActiveTutor, replyAppeal, getPracticeLanguages } from '../api/supabaseAPI';
import Loader from '../components/Loader';
import Message from '../components/Message';
import ConfirmDialog from '../components/ConfirmDialog';
import AppealChatModal from '../components/AppealChatModal';
import '../styles/AdminDashboard.css';

const R2_PUBLIC_BASE_URL_ENV = (import.meta.env.VITE_R2_PUBLIC_BASE_URL || '').replace(/\/$/, '');

export default function AdminDashboard() {
  const queryClient = useQueryClient();
  const supabase = useSupabaseClient();
  
  // ✨ Refactor activeTab to use URL Search Params
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'applications'; // Default to 'applications'

  // ✨ Persist Tab Selection
  const STORAGE_KEY_ADMIN = 'last_active_tab_admin';

  // Restore tab on mount if URL param is missing
  useEffect(() => {
    if (!searchParams.get('tab')) {
      const savedTab = localStorage.getItem(STORAGE_KEY_ADMIN);
      if (savedTab) {
        setSearchParams({ tab: savedTab }, { replace: true });
      }
    }
  }, []); // Run once on mount

  // Save tab whenever it changes, but only if explicitly set in URL
  useEffect(() => {
    if (searchParams.get('tab')) {
      localStorage.setItem(STORAGE_KEY_ADMIN, activeTab);
    }
  }, [activeTab, searchParams]);

  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedTutorKey, setSelectedTutorKey] = useState(null); // ✨ Only store key (uid-language)
  const [previewImage, setPreviewImage] = useState(null); // ✨ Lightbox State
  const [isRejecting, setIsRejecting] = useState(false); // ✨ Reject Modal State
  const [isPausing, setIsPausing] = useState(false); // ✨ Pause Modal State
  const [isRejectingTutor, setIsRejectingTutor] = useState(false); // ✨ Reject Tutor Modal
  const [actionReason, setActionReason] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [message, setMessage] = useState({ text: '', type: '' }); // ✨ Toast Message
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null }); // ✨ Custom Confirm Dialog
  const [showChatModal, setShowChatModal] = useState(false); // ✨ Chat Modal State

  // Fetch Public Base URL for Images
  const { data: r2PublicBaseUrl } = useQuery({
    queryKey: ['public_settings', 'r2_public_base_url'],
    queryFn: () => getPublicSetting('r2_public_base_url'),
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });
  const { data: practiceLanguages = [], isLoading: isLoadingPracticeLanguages } = useQuery({
    queryKey: ['practiceLanguages'],
    queryFn: getPracticeLanguages,
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });

  const publicBaseUrl = useMemo(() => {
    const rawBaseUrl = R2_PUBLIC_BASE_URL_ENV || r2PublicBaseUrl || '';
    return rawBaseUrl ? rawBaseUrl.replace(/\/$/, '') : '';
  }, [r2PublicBaseUrl]);

  const practiceLanguageMap = useMemo(() => (
    new Map(practiceLanguages.map((item) => [item.value, item.label]))
  ), [practiceLanguages]);

  // Fetch Pending Applications
  const { data: applications, isLoading: isLoadingApps } = useQuery({
    queryKey: ['admin', 'pendingApplications'],
    queryFn: getAdminPendingApplications,
    staleTime: 0,  // ✨ 永遠視為過期
    refetchOnMount: 'always',  // ✨ 每次進入頁面都重新請求
  });

  // Fetch Active Tutors
  const { data: tutors, isLoading: isLoadingTutors } = useQuery({
    queryKey: ['admin', 'activeTutors'],
    queryFn: getAdminTutors,
    enabled: activeTab === 'tutors',
    staleTime: 0,  // ✨ 永遠視為過期
    refetchOnMount: 'always',  // ✨ 每次進入頁面都重新請求
  });

  // ✨ Realtime subscription for new applications
  useEffect(() => {
    const channel = supabase
      .channel('admin-applications-updates')
      .on(
        'postgres_changes',
        {
          event: '*',  // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'tutor_applications'
        },
        () => {
          // 當有任何申請變化時，刷新資料
          queryClient.invalidateQueries({ queryKey: ['admin', 'pendingApplications'] });
          queryClient.invalidateQueries({ queryKey: ['admin', 'activeTutors'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [supabase, queryClient]);

  // ✨ Realtime subscription for tutor profile changes
  useEffect(() => {
    const channel = supabase
      .channel('admin-tutors-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tutors_profile'
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['admin', 'activeTutors'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [supabase, queryClient]);

  // ✨ 從 tutors 資料中動態取得 selectedTutor
  const selectedTutor = useMemo(() => {
    if (!selectedTutorKey || !tutors) return null;
    return tutors.find(t => `${t.uid}-${t.language}` === selectedTutorKey);
  }, [selectedTutorKey, tutors]);

  const approveMutation = useMutation({
    mutationFn: async ({ uid, language }) => {
      // 1. Find the old profile data from the cache to get its proof images
      const allTutors = queryClient.getQueryData(['admin', 'activeTutors']) || [];
      const existingProfile = allTutors.find(t => t.uid === uid && t.language === language);

      // 2. If the previous status was 'rejected', force delete its R2 files
      if (existingProfile && existingProfile.status === 'rejected' && existingProfile.proof_images) {
        try {
          const pathsToDelete = Array.isArray(existingProfile.proof_images)
            ? existingProfile.proof_images.map(img => img.path || img)
            : [];
          
          if (pathsToDelete.length > 0) {
            await supabase.functions.invoke('delete_proof_images', {
              body: { paths: pathsToDelete }
            });
          }
        } catch (err) {
          console.warn("R2 Cleanup failed, but proceeding with approval:", err);
        }
      }

      // 3. Perform actual approval
      return approveTutorApplication({ uid, language });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] });
      setMessage({ text: `Application for ${selectedApp.full_name} approved!`, type: 'success' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      setSelectedApp(null);
    },
    onError: (error) => {
      setMessage({ text: error.message, type: 'error' });
    }
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ uid, language, reason }) => {
      // 1. Find old profile for cleanup (Rejected -> Rejected case)
      const allTutors = queryClient.getQueryData(['admin', 'activeTutors']) || [];
      const existingProfile = allTutors.find(t => t.uid === uid && t.language === language);

      if (existingProfile && existingProfile.status === 'rejected' && existingProfile.proof_images) {
        try {
          const pathsToDelete = Array.isArray(existingProfile.proof_images)
            ? existingProfile.proof_images.map(img => img.path || img)
            : [];
          
          if (pathsToDelete.length > 0) {
            await supabase.functions.invoke('delete_proof_images', {
              body: { paths: pathsToDelete }
            });
          }
        } catch (err) {
          console.warn("R2 Cleanup failed, but proceeding with rejection:", err);
        }
      }

      return rejectTutorApplication({ uid, language, reason });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] });
      setMessage({ text: `Application rejected.`, type: 'info' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      setIsRejecting(false);
      setSelectedApp(null);
    },
    onError: (error) => {
      setMessage({ text: error.message, type: 'error' });
    }
  });

  // ✨ Pause Tutor Mutation
  const pauseMutation = useMutation({
    mutationFn: pauseTutor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] });
      setMessage({ text: `Tutor paused.`, type: 'info' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      setIsPausing(false);
      setSelectedTutorKey(null);
    },
    onError: (error) => {
      setMessage({ text: error.message, type: 'error' });
    }
  });

  // ✨ Reactivate Tutor Mutation
  const reactivateMutation = useMutation({
    mutationFn: reactivateTutor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] });
      setMessage({ text: `Tutor reactivated.`, type: 'success' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      setSelectedTutorKey(null);
    },
    onError: (error) => {
      setMessage({ text: error.message, type: 'error' });
    }
  });

  // ✨ Reject Active Tutor Mutation
  const rejectTutorMutation = useMutation({
    mutationFn: rejectActiveTutor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] });
      setMessage({ text: `Tutor rejected.`, type: 'info' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      setIsRejectingTutor(false);
      setSelectedTutorKey(null);
    },
    onError: (error) => {
      setMessage({ text: error.message, type: 'error' });
    }
  });

  // ✨ Reply Appeal Mutation with Optimistic UI
  const replyAppealMutation = useMutation({
    mutationFn: replyAppeal,
    onMutate: async (newReply) => {
      // 1. Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['admin', 'activeTutors'] });

      // 2. Snapshot previous value
      const previousTutors = queryClient.getQueryData(['admin', 'activeTutors']);

      // 3. Optimistic update
      queryClient.setQueryData(['admin', 'activeTutors'], (old) => {
        if (!old) return [];
        return old.map((tutor) => {
          if (tutor.uid === newReply.uid && tutor.language === newReply.language) {
            const newMessage = {
              role: 'admin', // I am admin
              message: newReply.message,
              at: new Date().toISOString(),
            };
            return {
              ...tutor,
              appeal_message: tutor.appeal_message ? [...tutor.appeal_message, newMessage] : [newMessage],
            };
          }
          return tutor;
        });
      });

      return { previousTutors };
    },
    onError: (err, newReply, context) => {
      queryClient.setQueryData(['admin', 'activeTutors'], context.previousTutors);
      setMessage({ text: err.message || 'Failed to send reply.', type: 'error' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'activeTutors'] });
    },
  });

  const formatTime = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getImageUrl = (path) => {
    if (!path) return '/Resources/imagine/default_avatar/default_avatar.jpeg'; // ✨ Default Avatar
    if (path.startsWith('http')) return path; // Already a full URL
    return `${publicBaseUrl}/${path}`;
  };

  const handleApprove = () => {
    if (!selectedApp) return;
    
    setConfirmDialog({
      isOpen: true,
      title: 'Approve Application',
      message: `Are you sure you want to approve ${selectedApp.full_name} as a tutor? This will immediately activate their profile.`,
      onConfirm: () => {
        approveMutation.mutate({ uid: selectedApp.uid, language: selectedApp.language });
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      }
    });
  };

  const handleRejectClick = () => {
    setIsRejecting(true);
    setActionReason(''); // Reset reason
  };

  const confirmReject = () => {
    if (!actionReason.trim()) {
      alert("Please provide a rejection reason.");
      return;
    }
    rejectMutation.mutate({ 
        uid: selectedApp.uid, 
        language: selectedApp.language, 
        reason: actionReason 
    });
  };

  // ✨ Tutor Management Handlers
  const handlePauseClick = () => {
    setIsPausing(true);
    setActionReason('');
  };

  const confirmPause = () => {
    if (!actionReason.trim()) {
      alert("Please provide a reason for pausing.");
      return;
    }
    pauseMutation.mutate({
      uid: selectedTutor.uid,
      language: selectedTutor.language,
      reason: actionReason
    });
  };

  const handleReactivate = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Reactivate Tutor',
      message: `Are you sure you want to reactivate ${selectedTutor.full_name}? They will be able to accept students again.`,
      onConfirm: () => {
        reactivateMutation.mutate({ uid: selectedTutor.uid, language: selectedTutor.language });
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      }
    });
  };

  const handleRejectTutorClick = () => {
    setIsRejectingTutor(true);
    setActionReason('');
  };

  const confirmRejectTutor = () => {
    if (!actionReason.trim()) {
      alert("Please provide a rejection reason.");
      return;
    }
    rejectTutorMutation.mutate({
      uid: selectedTutor.uid,
      language: selectedTutor.language,
      reason: actionReason
    });
  };

  // Handle Send from Modal
  const handleSendReply = (text) => {
    if (!selectedTutor) return;
    replyAppealMutation.mutate({
      uid: selectedTutor.uid,
      language: selectedTutor.language,
      message: text
    });
  };

  const formatAppealTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getLanguageLabel = (code) => {
    return practiceLanguageMap.get(code) || code?.replace('_', '-') || code;
  };

  const isLoading = (isLoadingApps && activeTab === 'applications')
    || (isLoadingTutors && activeTab === 'tutors')
    || isLoadingPracticeLanguages;

  return (
    <main className="admin-container">
      {/* Header */}
      <header className="admin-header">
        <h1 className="admin-title">Admin Dashboard</h1>
        {message.text && (
            <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 3000 }}>
                <Message text={message.text} type={message.type} />
            </div>
        )}
        
        <div className="admin-tabs">
          <button 
            className={`admin-tab-btn ${activeTab === 'applications' ? 'active' : ''}`}
            onClick={() => setSearchParams({ tab: 'applications' })}
          >
            <span>Applications</span>
            {(applications?.length || 0) > 0 ? (
              <span className="tab-count-badge">{applications.length}</span>
            ) : null}
          </button>
          <button 
            className={`admin-tab-btn ${activeTab === 'tutors' ? 'active' : ''}`}
            onClick={() => setSearchParams({ tab: 'tutors' })}
          >
            Active Tutors
          </button>
        </div>
      </header>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />

      {/* Content */}
      {isLoading ? (
        <Loader />
      ) : (
        <>
          {/* Applications Tab */}
          {activeTab === 'applications' && (
            <div className="admin-list-grid">
              {applications?.map(app => (
                <div key={`${app.uid}-${app.language}`} className="application-card">
                  <div className="app-card-header">
                    <div className="app-applicant">
                      <img 
                        src={getImageUrl(app.avatar)} 
                        alt="Avatar" 
                        className="app-avatar" 
                        style={{objectFit: 'cover'}}
                      />
                      
                      <div className="app-info">
                        <h3>{app.full_name}</h3>
                        <p>Applicant</p>
                      </div>
                    </div>
                    <span className="lang-badge">{app.language.replace('_', '-')}</span>
                  </div>
                  
                  <div className="app-time">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    Applied: {formatTime(app.applied_at)}
                  </div>

                  <button className="view-btn" onClick={() => setSelectedApp(app)}>
                    Review Application
                  </button>
                </div>
              ))}
              {(!applications || applications.length === 0) && (
                <div className="empty-state">No pending applications.</div>
              )}
            </div>
          )}

          {/* Tutors Tab */}
          {activeTab === 'tutors' && (
            <div className="admin-list-grid">
              {tutors?.map(tutor => {
                // Calculate unread count (consecutive messages from user at the end)
                let unreadCount = 0;
                const appeals = tutor.appeal_message || [];
                for (let i = appeals.length - 1; i >= 0; i--) {
                  if (appeals[i].role === 'admin') break; // Stop counting once we hit an admin reply
                  unreadCount++;
                }

                return (
                  <div key={`${tutor.uid}-${tutor.language}`} className="application-card">
                    <div className="app-card-header">
                      <div className="app-applicant">
                        <div style={{ position: 'relative' }}>
                          <img 
                            src={getImageUrl(tutor.avatar)} 
                            alt="Avatar" 
                            className="app-avatar" 
                            style={{objectFit: 'cover'}}
                          />
                          {unreadCount > 0 && (
                            <span className="notification-dot">
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                          )}
                        </div>
                        <div className="app-info">
                          <h3>{tutor.full_name}</h3>
                          <p>{tutor.headline || 'No headline'}</p>
                        </div>
                      </div>
                      <span className={`lang-badge ${tutor.status === 'paused' ? 'status-paused' : ''}`}>
                        {tutor.language.replace('_', '-')}
                      </span>
                    </div>
                    
                    <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span className={`tutor-status tutor-status-${tutor.status}`}>
                        ● {tutor.status}
                      </span>
                      <button 
                        className="view-btn" 
                        style={{width: 'auto', padding: '6px 12px', fontSize: '0.8rem'}}
                        onClick={() => setSelectedTutorKey(`${tutor.uid}-${tutor.language}`)}
                      >
                        Manage
                      </button>
                    </div>
                  </div>
                );
              })}
              {(!tutors || tutors.length === 0) && (
                <div className="empty-state">No tutors found.</div>
              )}
            </div>
          )}
        </>
      )}

      {/* Review Modal */}
      {selectedApp && (
        <div className="modal-overlay" onClick={() => setSelectedApp(null)}>
          <div className="review-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <h2>Review Application</h2>
                <div style={{display: 'flex', gap: '8px', marginTop: '4px', fontSize: '0.9rem', color: 'var(--text-light)'}}>
                  <span>{selectedApp.full_name}</span>
                  <span>•</span>
                  <span>{getLanguageLabel(selectedApp.language)}</span>
                </div>
              </div>
              <button className="modal-close" onClick={() => setSelectedApp(null)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="modal-content">
              {/* ✨ Warning for Previously Rejected Applicants */}
              {selectedApp.history_status === 'rejected' && (
                <div className="review-section rejection-reason-section">
                  <h4>
                    ⚠️ Previously Rejected
                  </h4>
                  <div className="rejection-reason-text">
                    This applicant was previously rejected. Reason: <strong>{selectedApp.history_reason || 'No reason provided'}</strong>
                  </div>
                </div>
              )}

              <div className="review-section">
                <h4>Self Introduction</h4>
                <div className="intro-text">
                  {selectedApp.self_introduction}
                </div>
              </div>

              <div className="review-section">
                <h4>Proof Documents</h4>
                <div className="proof-grid">
                  {selectedApp.proof_images?.map((img, idx) => (
                    <div 
                      key={idx} 
                      className="proof-item"
                      onClick={() => setPreviewImage(getImageUrl(img.path))} // ✨ Open Lightbox
                    >
                      {/* Use getImageUrl helper for proof paths */}
                      <img src={getImageUrl(img.path)} alt={img.name} />
                      <div className="proof-label">{img.name}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="action-btn btn-reject" onClick={handleRejectClick}>
                Reject Application
              </button>
              <button className="action-btn btn-approve" onClick={handleApprove}>
                Approve & Activate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✨ Lightbox Overlay */}
      {previewImage && (
        <div className="lightbox-overlay" onClick={() => setPreviewImage(null)}>
          <img 
            src={previewImage} 
            alt="Full Preview" 
            className="lightbox-img" 
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking on the image itself
          />
          <button 
            className="modal-close" 
            style={{position: 'absolute', top: '20px', right: '20px', color: 'white', background: 'rgba(255,255,255,0.2)'}}
            onClick={() => setPreviewImage(null)}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      )}

      {/* ✨ Reject Confirmation Modal */}
      {isRejecting && (
        <div className="mini-modal-overlay">
          <div className="mini-modal">
            <h3>Reject Application</h3>
            <p style={{fontSize: '0.9rem', color: 'var(--text-medium)'}}>
              Please provide a reason for rejecting this application. This will be visible to the applicant.
            </p>
            <textarea
              className="reject-textarea"
              placeholder="e.g. Identity document is blurry..."
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              autoFocus
            />
            <div className="mini-modal-actions">
              <button className="btn-ghost" onClick={() => setIsRejecting(false)}>Cancel</button>
              <button className="action-btn btn-reject" style={{padding: '8px 16px'}} onClick={confirmReject}>
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✨ Manage Tutor Modal */}
      {selectedTutor && (
        <div className="modal-overlay" onClick={() => setSelectedTutorKey(null)}>
          <div className="review-modal manage-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <h2>Manage Tutor</h2>
                <div style={{display: 'flex', gap: '8px', marginTop: '4px', fontSize: '0.9rem', color: 'var(--text-light)'}}>
                  <span>{selectedTutor.full_name}</span>
                  <span>•</span>
                  <span>{getLanguageLabel(selectedTutor.language)}</span>
                  <span>•</span>
                  <span className={`tutor-status tutor-status-${selectedTutor.status}`}>
                    {selectedTutor.status}
                  </span>
                </div>
              </div>
              <button className="modal-close" onClick={() => setSelectedTutorKey(null)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="modal-content">
              {/* ✨ Reason Section (Paused / Rejected / History) */}
              {selectedTutor.reason && (
                <div className="review-section pause-reason-section">
                  <h4>
                    {selectedTutor.status === 'paused' ? 'Pause Reason' : 
                     selectedTutor.status === 'rejected' ? 'Rejection Reason' : 
                     'Status Reason'}
                  </h4>
                  <div className="pause-reason-text">{selectedTutor.reason}</div>
                </div>
              )}

              {/* Self Introduction */}
              <div className="review-section">
                <h4>Self Introduction</h4>
                <div className="intro-text">
                  {selectedTutor.self_introduction || 'No introduction provided.'}
                </div>
              </div>

              {/* Proof Documents */}
              <div className="review-section">
                <h4>Proof Documents</h4>
                <div className="proof-grid">
                  {selectedTutor.proof_images?.map((img, idx) => (
                    <div 
                      key={idx} 
                      className="proof-item"
                      onClick={() => setPreviewImage(getImageUrl(img.path))}
                    >
                      <img src={getImageUrl(img.path)} alt={img.name} />
                      <div className="proof-label">{img.name}</div>
                    </div>
                  ))}
                  {(!selectedTutor.proof_images || selectedTutor.proof_images.length === 0) && (
                    <p style={{color: 'var(--text-light)'}}>No documents available.</p>
                  )}
                </div>
              </div>

              {/* ✨ Replaced Appeal Messages with Button */}
              {selectedTutor.status === 'paused' && (
                <div className="review-section">
                  <h4>Appeal Messages</h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: 'var(--background-light)', padding: '1rem', borderRadius: '12px' }}>
                    <div style={{ flex: 1 }}>
                       <p style={{ margin: 0, fontWeight: 600, color: 'var(--text-dark)' }}>
                         {selectedTutor.appeal_message?.length || 0} messages
                       </p>
                       <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-light)' }}>
                         View conversation history and reply
                       </p>
                    </div>
                    <button 
                      className="action-btn btn-approve" 
                      style={{ padding: '8px 16px' }}
                      onClick={() => setShowChatModal(true)}
                    >
                      View Appeal Chat
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              {selectedTutor.status === 'active' && (
                <>
                  <button className="action-btn btn-pause" onClick={handlePauseClick}>
                    Pause Tutor
                  </button>
                  <button className="action-btn btn-reject" onClick={handleRejectTutorClick}>
                    Reject Tutor
                  </button>
                </>
              )}
              {selectedTutor.status === 'paused' && (
                <>
                  <button className="action-btn btn-approve" onClick={handleReactivate}>
                    Reactivate
                  </button>
                  <button className="action-btn btn-reject" onClick={handleRejectTutorClick}>
                    Reject Tutor
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ✨ Pause Tutor Modal */}
      {isPausing && (
        <div className="mini-modal-overlay">
          <div className="mini-modal">
            <h3>Pause Tutor</h3>
            <p style={{fontSize: '0.9rem', color: 'var(--text-medium)'}}>
              Please provide a reason for pausing this tutor. The tutor can appeal this decision.
            </p>
            <textarea
              className="reject-textarea"
              placeholder="e.g. We need to verify your updated credentials..."
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              autoFocus
            />
            <div className="mini-modal-actions">
              <button className="btn-ghost" onClick={() => setIsPausing(false)}>Cancel</button>
              <button className="action-btn btn-pause" style={{padding: '8px 16px'}} onClick={confirmPause}>
                Confirm Pause
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✨ Reject Tutor Modal */}
      {isRejectingTutor && (
        <div className="mini-modal-overlay">
          <div className="mini-modal">
            <h3>Reject Tutor</h3>
            <p style={{fontSize: '0.9rem', color: 'var(--text-medium)'}}>
              This will permanently revoke the tutor's status. They will need to reapply. Please provide a reason.
            </p>
            <textarea
              className="reject-textarea"
              placeholder="e.g. Violation of terms of service..."
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              autoFocus
            />
            <div className="mini-modal-actions">
              <button className="btn-ghost" onClick={() => setIsRejectingTutor(false)}>Cancel</button>
              <button className="action-btn btn-reject" style={{padding: '8px 16px'}} onClick={confirmRejectTutor}>
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✨ Appeal Chat Modal (New Component) */}
      <AppealChatModal
        isOpen={showChatModal && !!selectedTutor}
        onClose={() => setShowChatModal(false)}
        title={selectedTutor?.full_name || 'Chat'}
        subtitle={getLanguageLabel(selectedTutor?.language)}
        messages={selectedTutor?.appeal_message || []}
        onSendMessage={handleSendReply}
        isLoading={replyAppealMutation.isLoading}
        currentUserRole="admin" // ✨ Tells the modal that 'admin' messages are MINE (Right side)
      />
    </main>
  );
}
